/**
 * Mirror backend/utils/village_slug.py — admin URLs, deep links, share canonicals.
 * Strips Facebook/Messenger paste junk (spaces + marketing text after the slug).
 */

const VILLAGE_SLUG_ALIASES = {
  "mosciano-santangelo": "mosciano-sant-angelo",
};

/** Trailing marketing / paste noise glued with - or _ (not separated by space). */
const TRAILING_MARKETING =
  /(?:-|_)+(?:unione[-_]?comuni[-_]?amici|comuni[-_]?amici|6amico)$/i;

/**
 * Decode and take the first path-like slug token from a dirty route param.
 * e.g. "assisi unione comuni amici" → "assisi"
 *      "assisi%20unione%20comuni%20amici" → "assisi"
 *      "campli__castelnuovo extra" → "campli__castelnuovo"
 */
export function normalizeSlugInput(slug) {
  let raw = String(slug ?? "").trim();
  if (!raw) return "";
  try {
    raw = decodeURIComponent(raw.replace(/\+/g, " "));
  } catch {
    raw = raw.replace(/\+/g, " ");
  }
  raw = raw.trim();
  if (raw.includes("/")) {
    const parts = raw.split("/").filter(Boolean);
    raw = parts[parts.length - 1] || raw;
  }
  // Facebook / Messenger: URL then space + caption ("unione comuni amici")
  const token = (raw.split(/\s+/)[0] || "").trim();
  let cleaned = token.replace(TRAILING_MARKETING, "").replace(/^[-_]+|[-_]+$/g, "");
  return cleaned.toLowerCase();
}

export function resolveVillageSlug(slug) {
  const raw = normalizeSlugInput(slug);
  if (!raw) return "";
  return VILLAGE_SLUG_ALIASES[raw] || raw;
}

/**
 * Sanitize a pathname for share/canonical URLs.
 * Keeps structural segments (comune, citta, frazione, …); cleans slug segments.
 */
export function sanitizeSharePathname(pathname) {
  const path = String(pathname || "/").trim() || "/";
  const parts = path.split("/");
  const structural = new Set([
    "",
    "comune",
    "citta",
    "città",
    "frazione",
    "admin",
    "api",
    "app",
    "mobile",
    "parrocchia",
    "vetrina",
    "archivio",
  ]);
  return parts
    .map((seg) => {
      if (!seg || structural.has(seg.toLowerCase())) return seg;
      return resolveVillageSlug(seg) || seg;
    })
    .join("/");
}
